/* eslint-disable camelcase */
import Loader from '../../components/Loader';
import {T} from '../../utils/I18n/I18n';
import {useTranslation} from "react-i18next";
import React, { useContext, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import FormInput from '../../components/UI/FormInput/FormInput';
import { useCartContext } from '../../context/CartContext';
import { useUserContext } from '../../context/UserContext';
import useSetGuestEmailOnCart from '../../hooks/useSetGuestEmail';
import useSetShippingAddress from '../../hooks/useSetShippingAddress';
import useSetShippingMethod from '../../hooks/useSetShippingMethod';
import { countryCode, METHOD_MATRIX } from '../../utils/checkout';
import { addShippingInfoEvent } from '../../hooks/useAnalytics';
import CheckoutAddressItem from './CheckoutAddressItem/CheckoutAddressItem';
import CheckoutEditAddress from './CheckoutAddressItem/CheckoutEditAddress';
import NewAddressForm from './CheckoutAddressItem/NewAddressForm';
import PostcodeAndCity from './CheckoutAddressItem/SelectCityByPostcode/PostcodeAndCity';
import CheckoutEmailOrLogin from './CheckoutEmailOrLogin';
import CheckoutShippingOptions from './CheckoutShippingOptions';
import {usePopup} from "../../context/PopupContext";

const CheckoutDeliveryForm = ({ setIsDisabled }) => {
  const { register, handleSubmit, errors, setValue } = useForm();
  const setGuestEmail = useSetGuestEmailOnCart();
  const { setShippingAddress } = useSetShippingAddress();
  const { setShippingMethod } = useSetShippingMethod();

  const { t } = useTranslation();
  const { cart } = useCartContext();
  const { user } = useUserContext();

  const [email, setEmail] = useState(cart.email);

  const shippingAddress = cart && cart.shipping_addresses && cart.shipping_addresses[0];
  const shippingMethod = shippingAddress && shippingAddress.selected_shipping_method;
  const isMatrixRate = shippingMethod && shippingMethod.carrier_code === METHOD_MATRIX;

  const [addressId, setAddressId] = useState('');
  const [editAddressId, setEditAddressId] = useState('');
  const [savedAddress, setSavedAddress] = useState({});
  const [isAdded, setIsAdded] = useState(false);
  const [addedNewAddress, setAddedNewAddress] = useState(false);
  const [calculateButtonLoading, setCalculateButtonLoading] = useState(false);
  const [isDirty, setIsDirty] = useState(!shippingAddress || !isMatrixRate);
  const [savedToAddressBook, setSavedToAddressBook] = useState({
    added: false,
    identifier: '',
  });
  const { showAddToCartPopupMessage } = usePopup();
  const disableCalculateButton = !addressId && !savedAddress.firstname;

  useEffect(() => {
    //checkoutOptionEvent(2, 'Standard Delivery');
    addShippingInfoEvent(cart.items, 'Standard Delivery')
  }, []);

  useEffect(() => {
    if (shippingAddress && isMatrixRate) {
      setValue('email', cart.email);
      setValue('firstname', shippingAddress.firstname);
      setValue('lastname', shippingAddress.lastname);
      setValue('street', shippingAddress.street[0]);
      setValue('postcode', shippingAddress.postcode);
      setValue('city', shippingAddress.city);
      setValue('country_code', shippingAddress.country.code);
      setValue('telephone', shippingAddress.telephone);

      if (shippingAddress.customer_address_id) {
        setAddressId(+shippingAddress.customer_address_id);
      } else {
        const newSavedAddress = {
          ...shippingAddress,
          country_code: shippingAddress.country.code,
          id: 'savedAddress',
        };

        // eslint-disable-next-line no-underscore-dangle
        delete newSavedAddress.__typename;
        delete newSavedAddress.region;
        delete newSavedAddress.country;
        delete newSavedAddress.pickup_location_code;
        delete newSavedAddress.selected_shipping_method;
        delete newSavedAddress.customer_address_id;
        delete newSavedAddress.available_shipping_methods;

        setIsAdded(true);
        setAddedNewAddress(true);
        setAddressId('savedAddress');
        setSavedAddress(newSavedAddress);
      }
    }
  }, [shippingAddress]);

  useEffect(() => {
    setIsDisabled(!isMatrixRate);
  }, []);

  /** @type {React.FormEventHandler<HTMLFormElement>} */
  const onCalculateDelivery = async formData => {
    if (formData.password === '' || formData.password) {
      delete formData.password;
    }

    const setShipping = () => {
      if (user && addressId === 'savedAddress') {
        delete savedAddress.id;
        setSavedAddress({ ...savedAddress, id: 'savedAddress' });
        return setShippingAddress(savedAddress);
      }

      if (user && addressId !== '') {
        return setShippingAddress(undefined, addressId);
      }

      return setShippingAddress(formData);
    };

    try {
      setCalculateButtonLoading(true);

      if (!user) {
        await setGuestEmail(formData.email);
        delete formData.email;
      }

      const { data } = await setShipping();

      const newCart = data.setShippingAddressesOnCart.cart;
      const methods = newCart.shipping_addresses[0].available_shipping_methods;
      const matrixrateMethods = methods.filter(method => method.carrier_code === METHOD_MATRIX);
      if (matrixrateMethods.length === 0) {
        showAddToCartPopupMessage(t('checkout.islandDelivery'), 'error', 4000);
        setIsDirty(true);
      }else{
        const [{ carrier_code, method_code }] = matrixrateMethods;
        await setShippingMethod({ carrier_code, method_code });
        setIsDisabled(false);
        setIsDirty(false);

      }


    } finally {
      setCalculateButtonLoading(false);
    }
  };

  return (
    <div>
      {user && user.addresses && (
        <CheckoutAddressItem
          addresses={user.addresses}
          addressId={addressId}
          setAddressId={setAddressId}
          savedAddress={savedAddress}
          setEditAddressId={setEditAddressId}
          savedToAddressBook={savedToAddressBook}
          setIsDirty={setIsDirty}
        />
      )}

      {user && !isAdded && editAddressId === '' && (
        // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
        <p className="checkout-address-item__add-address" onClick={() => setIsAdded(true)}>
          <T id="address.addAddress" />
        </p>
      )}

      {user && isAdded && !addedNewAddress && (
        <NewAddressForm
          setSavedAddress={setSavedAddress}
          setIsAdded={setIsAdded}
          setSavedToAddressBook={setSavedToAddressBook}
          setAddressId={setAddressId}
          setAddedNewAddress={setAddedNewAddress}
          setIsDirty={setIsDirty}
        />
      )}

      {user && editAddressId !== '' && (
        <CheckoutEditAddress
          setSavedAddress={setSavedAddress}
          editAddressId={editAddressId}
          setEditAddressId={setEditAddressId}
          savedToAddressBook={savedToAddressBook}
          setSavedToAddressBook={setSavedToAddressBook}
          setIsDisabled={setIsDisabled}
          setAddressId={setAddressId}
          addresses={user.addresses}
          savedAddress={savedAddress}
          setIsDirty={setIsDirty}
        />
      )}

      {!user && (
        <form onSubmit={handleSubmit(onCalculateDelivery)} onChange={() => setIsDirty(true)}>
          <CheckoutEmailOrLogin register={register} errors={errors} email={email} setEmail={setEmail}/>

          <FormInput
            register={register}
            type="text"
            text={<T id="registration.name" />}
            name="firstname"
            errors={errors}
          />

          <FormInput
            register={register}
            type="text"
            text={<T id="registration.lastName" />}
            name="lastname"
            errors={errors}
          />

          <FormInput register={register} type="text" text={<T id="address.street" />} name="street" errors={errors} />

          <PostcodeAndCity {...{ register, errors, setValue }} />

          <FormInput
            register={register}
            errors={errors}
            text={<T id="address.country" />}
            name="country_code"
            defaultValue={countryCode}
          >
            {countryCode === 'HR' && <option value="HR">Hrvatska</option>}
            {countryCode === 'RS' && <option value="RS">Srbija</option>}
            {countryCode === 'SI' && <option value="SI">Slovenija</option>}
          </FormInput>

          <FormInput
            register={register}
            type="number"
            text={<T id="cartSummary.cellphoneNumber" />}
            name="telephone"
            errors={errors}
          />

          {isDirty && (
            <div className="calculate-button-wrapper">
              {calculateButtonLoading ? (
                <Loader height="xxl" />
              ) : (
                <button className="checkout__main__form__button" title={t('cartSummary.calculate')} type="submit">
                  <T id="cartSummary.calculate" />
                </button>
              )}
            </div>
          )}
        </form>
      )}

      {user && isDirty && (
        <div className="calculate-button-wrapper">
          {calculateButtonLoading ? (
            <Loader height="xxl" />
          ) : (
            <button
              className="checkout__main__form__button"
              title={t('cartSummary.calculate')}
              type="button"
              onClick={onCalculateDelivery}
              disabled={disableCalculateButton}
            >
              <T id="cartSummary.calculate" />
            </button>
          )}
        </div>
      )}
      {!isDirty && <CheckoutShippingOptions setIsDisabled={setIsDisabled} carrierCode={METHOD_MATRIX} />}
    </div>
  );
};

export default CheckoutDeliveryForm;
